import axiosInstance from './axios';

export async function listTestRuns(controlUuid, auditYearId) {
  const params = auditYearId ? { audit_year_id: auditYearId } : {};
  const resp = await axiosInstance.get(`/api/control-test-runs/control/${controlUuid}`, { params });
  return resp.data?.runs || [];
}

/**
 * Every control's runs in the organization (the Test Runs page), one row per rerun chain:
 * { runs, total, page, page_size, total_pages }. Each run also carries `control` and `attempts`.
 */
export async function listOrgTestRuns(params) {
  const resp = await axiosInstance.get('/api/control-test-runs', { params });
  return resp.data;
}

export async function createTestRun(payload) {
  const resp = await axiosInstance.post('/api/control-test-runs', payload);
  return resp.data;
}

export async function updateTestRun(runId, payload) {
  const resp = await axiosInstance.put(`/api/control-test-runs/${runId}`, payload);
  return resp.data;
}

export async function deleteTestRun(runId) {
  await axiosInstance.delete(`/api/control-test-runs/${runId}`);
}

export async function getTestRun(runId) {
  const resp = await axiosInstance.get(`/api/control-test-runs/${runId}`);
  return resp.data;
}

/**
 * Replace the run's evidence-file samples (runs where population is not required).
 * `labels` is the full ordered list; several evidence files can be tagged with one label.
 * Returns the updated run.
 */
export async function syncEvidenceSamples(runId, labels) {
  const resp = await axiosInstance.put(`/api/control-test-runs/${runId}/evidence-samples`, { labels });
  return resp.data;
}

/** Save the reason a population was not used (asked on the results page). */
export async function updatePopulationSkipReason(runId, reason) {
  const resp = await axiosInstance.patch(`/api/control-test-runs/${runId}/population-skip-reason`, { reason });
  return resp.data;
}

/**
 * What the Run button should know: { ready, issues[{code,message}], warnings[{code,message,samples}] }.
 * `issues` are the problems the server refuses to execute with; `warnings` are advisory.
 */
export async function getRunReadiness(runId) {
  const resp = await axiosInstance.get(`/api/control-test-runs/${runId}/readiness`);
  return resp.data;
}

/**
 * The saved activity log of a run: { run_id, outcome, entries, summary, updated_at }, or null when the run
 * has none (runs executed before the log existed, or a log that was never saved).
 */
export async function getRunActivityLog(runId) {
  try {
    const resp = await axiosInstance.get(`/api/control-test-runs/${runId}/activity-log`);
    return resp.data;
  } catch (err) {
    if (err?.response?.status === 404) return null;
    throw err;
  }
}

/** The recorded text of a run's model calls ({ run_id, count, calls, updated_at }), or null when none was recorded. */
export async function getRunRawCalls(runId) {
  try {
    const resp = await axiosInstance.get(`/api/control-test-runs/${runId}/activity-log/raw`);
    return resp.data;
  } catch (err) {
    if (err?.response?.status === 404) return null;
    throw err;
  }
}

/** Workflow statuses of a run that has results (executed, possibly in review or signed off). */
export const FINISHED_RUN_STATUSES = ['completed', 'failed', 'signed_off', 'prepared', 'in_review'];

/**
 * A control's executed control-testing runs (test_runs) for the issue screens — linking test runs to a new
 * issue and picking the run that verifies remediation — newest first, in the shape those screens read:
 * { id, test_run_id, test_run_number, test_run_name, workflow_status, overall_status, total_exceptions,
 *   total_population }.
 * Like the Test Runs page it offers only the newest attempt of each rerun chain, and no copies of
 * classic test executions.
 */
export async function listIssueRunOptions(controlUuid, auditYearId) {
  const runs = await listTestRuns(controlUuid, auditYearId);
  return pickIssueRuns(runs).map((r) => toIssueRunOption(r));
}

const isFinishedRun = (r) => FINISHED_RUN_STATUSES.includes(String(r.workflow_status || '').toLowerCase());

/**
 * One run per rerun chain for the issue screens: the newest attempt that has results. A newer
 * attempt that has not run yet (a pending rerun) does not hide a finished one. Copies of classic
 * test executions are left out. Newest first.
 */
export function pickIssueRuns(runs) {
  const live = (runs || []).filter((r) => r.legacy_execution_id == null);
  const byId = new Map(live.map((r) => [r.id, r]));
  const superseded = new Set(live.map((r) => r.supersedes_run_id).filter((id) => byId.has(id)));
  const picked = [];
  for (const head of live.filter((r) => !superseded.has(r.id))) {
    const seen = new Set();
    let cur = head;
    while (cur && !isFinishedRun(cur) && !seen.has(cur.id)) {
      seen.add(cur.id);
      cur = byId.get(cur.supersedes_run_id);
    }
    if (cur && isFinishedRun(cur)) picked.push(cur);
  }
  return picked.sort((a, b) => (b.run_number ?? 0) - (a.run_number ?? 0));
}

function toIssueRunOption(r) {
  const row = mapRunForList(r);
  return {
    id: r.id,
    test_run_id: r.id,
    test_run_number: r.run_number,
    test_run_name: r.test_run_name || null,
    workflow_status: r.workflow_status,
    overall_status: r.execution?.overall_status ?? null,
    total_exceptions: r.execution?.total_exceptions ?? null,
    total_population: row.total_population ?? null,
  };
}

const MAX_SAMPLES_LISTED = 3;

/**
 * Turn a readiness response into what to tell the user before running.
 *   blocking — set when the run cannot be executed (do not run);
 *   warning  — set when it can run but some samples have no evidence (run, but say so);
 *   issueCodes — the codes of the blocking issues, for choosing where to send the user.
 */
export function describeReadiness(readiness) {
  const issues = Array.isArray(readiness?.issues) ? readiness.issues : [];
  const warnings = Array.isArray(readiness?.warnings) ? readiness.warnings : [];

  const blocking = issues.length > 0
    ? `This run is not ready to execute: ${issues.map((i) => i.message).join('; ')}.`
    : null;

  const noEvidence = warnings.find((w) => w.code === 'samples_without_evidence');
  let warning = null;
  if (noEvidence) {
    const samples = noEvidence.samples || [];
    const shown = samples.slice(0, MAX_SAMPLES_LISTED).join(', ');
    const more = samples.length > MAX_SAMPLES_LISTED ? ` and ${samples.length - MAX_SAMPLES_LISTED} more` : '';
    warning = samples.length > 0
      ? `${noEvidence.message} (${shown}${more}).`
      : `${noEvidence.message}.`;
  }
  // Duplicate / mixed-up input files (population uploaded as evidence, IPE proof = population, same file twice).
  const inputWarnings = warnings.filter((w) => typeof w.code === 'string' && w.code.startsWith('input_'));
  return { blocking, warning, inputWarnings, issueCodes: issues.map((i) => i.code) };
}

/** Duplicate checks over a control's population, IPE proof and evidence files: {findings: [{code, severity, message, files}]}. */
export async function getInputChecks(controlUuid, { populationFileId, ipeProofFileId } = {}) {
  const resp = await axiosInstance.post(`/api/sox-controls/${controlUuid}/input-checks`, {
    population_file_id: populationFileId || 0,
    ipe_proof_file_id: ipeProofFileId || 0,
  });
  return Array.isArray(resp.data?.findings) ? resp.data.findings : [];
}

/** True when this run tests from evidence files instead of a population file. */
export function isPopulationNotRequired(r) {
  return r?.population_status === 'not_required';
}

/** Selected sample refs from a list/detail run row. */
export function getRunSelectedSamples(r) {
  const fromPop = r?.population_data?.selected_samples;
  if (Array.isArray(fromPop) && fromPop.length > 0) {
    return fromPop.map(String);
  }
  const fromSamples = r?.samples;
  if (Array.isArray(fromSamples) && fromSamples.length > 0) {
    return fromSamples.map((s) => String(s.record_ref || s)).filter(Boolean);
  }
  return [];
}

/** Map unified test_runs row to TestRunsTab list shape. */
export function mapRunForList(r) {
  const exec = r.execution || r.latest_execution || {};
  const selectedSamples = getRunSelectedSamples(r);
  const totalCount =
    r.population_data?.total_count ??
    r.population?.total_count ??
    r.total_population ??
    null;
  const sampleCount = r.sample_count ?? r.total_sample_size ?? selectedSamples.length;

  return {
    executionId: r.id,
    test_run_number: r.run_number,
    test_run_name: r.test_run_name,
    testStatus: r.workflow_status,
    status: r.workflow_status,
    steps_passed: exec.attributes_passed || 0,
    steps_failed: exec.attributes_failed || 0,
    steps_warning: exec.attributes_warning || 0,
    total_exceptions: exec.total_exceptions || 0,
    testing_period_start: r.testing_period_start,
    testing_period_end: r.testing_period_end,
    total_population: totalCount,
    total_sample_size: sampleCount,
    test_type: r.test_type,
    selected_periods: r.selected_periods,
    population_id: r.population_id,
    population_data: (() => {
      // Merge file details (population record) with sample/count fields.
      // Strip population record 'id' so it doesn't shadow the test run id.
      const { id: _popId, ...popFileDetails } = r.population || {};
      return {
        ...popFileDetails,            // file details: population_file_id/name, ipe_proof_file_id/name, etc.
        ...(r.population_data || {}), // sample/count fields override if present
        selected_samples: selectedSamples,
        total_count: totalCount,
        sample_size: sampleCount,
      };
    })(),
    samples: r.samples || [],
    sample_count: sampleCount,
    files_analyzed: exec.files_analyzed || [],
    isCurrent: r.is_current !== false,
    supersedesExecutionId: r.supersedes_run_id || null,
    legacy_execution_id: r.legacy_execution_id,
    ipe_validated: r.ipe_validated,
    population_status: r.population_status || 'provided',
    population_skip_reason: r.population_skip_reason ?? null,
    created_at: r.created_at,
    workflow_status: r.workflow_status,
    testing_method: r.testing_method,
  };
}

export function runHasPopulation(run) {
  // No population is expected for these runs — their samples are evidence files.
  if (isPopulationNotRequired(run)) return true;
  const pop = run?.population_data || {};
  return !!(pop.total_count || run?.total_population || run?.population_id);
}

export function runHasSamples(run) {
  const samples = getRunSelectedSamples(run);
  return samples.length > 0 || (run?.total_sample_size > 0) || (run?.sample_count > 0);
}
